import React from 'react'
import { Maybe } from '@graphql-tools/utils'
import { deleteCookie, getCookie, setCookie } from 'cookies-next'
import { useForm } from 'react-hook-form'
import { Button } from '@/atoms/Button'
import { DevicesIcon } from '@/atoms/Icons/DevicesIcon'
import { InternalLink } from '@/atoms/InternalLink'
import { AngelLogo } from '@/atoms/Logos/AngelLogo'
import { CaptionXS, TitleMD } from '@/atoms/Text'
import { LOGIN_USER_EMAIL } from '@/constants/cookies'
import { GUILD_SIGNUP_OFFER_ID_COOKIE, GUILD_SIGNUP_PROMO_CODE_COOKIE } from '@/constants/cookies'
import { paths } from '@/constants/paths'
import { AuthApi } from '@/services/Auth/AuthService'
import { buildLoginUrl } from '@/utils/BuildLoginUrl'
import { useSafeTrack } from '@/utils/analytics'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { LoginOrigin } from '@/views/GuildSignupView/GuildEmailInput/GuildEmailInput'

interface SignupCreateAccountEmailProps {
  onEmailSubmit: (email: string) => void
  loginStartedOrigin?: Maybe<LoginOrigin>
  loginRedirectUrl?: Maybe<string>
}

export const SignupCreateAccountEmail: React.FC<SignupCreateAccountEmailProps> = ({
  onEmailSubmit,
  loginRedirectUrl,
  loginStartedOrigin,
}) => {
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<{ email: string }>({
    defaultValues: { email: '' },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
  })
  const [submitError, setSubmitError] = React.useState<string | null>(null)
  const authService = React.useMemo(() => new AuthApi(), [])
  const track = useSafeTrack()
  const router = useSafeRouter()

  const offerId = getCookie(GUILD_SIGNUP_OFFER_ID_COOKIE)

  function getSignupRedirectUrl() {
    if (loginStartedOrigin === 'mercy-ships-multi-claim') {
      return '/m/shared/mercy-ships'
    }
    if (loginStartedOrigin === 'sacred-thread-multi-claim') {
      return '/m/shared/sacred-thread'
    }
    if (offerId) {
      const promoCode = getCookie(GUILD_SIGNUP_PROMO_CODE_COOKIE)
      deleteCookie(GUILD_SIGNUP_OFFER_ID_COOKIE)
      deleteCookie(GUILD_SIGNUP_PROMO_CODE_COOKIE)
      let checkoutUrl = `${paths.guild.checkout}/${offerId}`
      if (promoCode) {
        checkoutUrl += `?promoCode=${promoCode}`
      }
      return checkoutUrl
    }
    if (loginRedirectUrl) {
      return loginRedirectUrl
    }
    return paths.guild.signup.plan
  }

  const onSubmit = async ({ email }: { email: string }) => {
    setSubmitError(null)
    setCookie(LOGIN_USER_EMAIL, email)
    const userCheck = await fetch(`/api/authByApi/checkUser?email=${encodeURIComponent(email)}`)
    const userCheckJson = await userCheck.json()
    if (userCheckJson.exists) {
      const ellisOauthUrl = buildLoginUrl(getSignupRedirectUrl())
      router.push(ellisOauthUrl)
      return
    }
    try {
      await authService.sendVerificationCode(email)
      track('Guild Signup Email Submitted')
      onEmailSubmit(email)
    } catch (err) {
      track('Guild Signup Email Submission Error')
      setSubmitError('Failed to send verification code. Please try again.')
    }
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center">
      <div className="mx-auto flex w-full max-w-md flex-col items-center p-6">
        <AngelLogo height={40} color="black" className="mb-6" />
        <TitleMD weight="normal" className="mb-2">
          Create New Account
        </TitleMD>
        <DevicesIcon size={210} color={'oxide'} className="mb-6" />
        <form onSubmit={handleSubmit(onSubmit)} className="flex w-full flex-col">
          <label htmlFor="email" className="pt-6 font-medium text-core-gray-900">
            Email address
          </label>
          <input
            id="email"
            type="email"
            {...register('email', {
              required: 'Email is required',
              pattern: {
                value: /^[^@\s]+@[^@\s]+\.[^@\s]+$/,
                message: 'Please enter a valid email address.',
              },
            })}
            className={`w-full rounded-lg border border-core-gray-300 px-4 py-3 focus-visible:ring-0 focus-visible:ring-transparent focus-visible:ring-offset-0 focus-visible:ring-offset-transparent ${
              errors.email ? ' border-error-500' : ''
            }`}
            placeholder="Email address"
          />
          <CaptionXS role="alert" className="min-h-[20px] !text-error-500">
            {errors.email ? errors.email.message : <span>&nbsp;</span>}
          </CaptionXS>
          <CaptionXS role="alert" className="min-h-[20px] !text-error-500">
            {submitError ? submitError : <span>&nbsp;</span>}
          </CaptionXS>
          <Button
            type="submit"
            disabled={isSubmitting}
            variant="core-gray-900"
            className="w-full cursor-pointer py-3 font-semibold"
          >
            Continue
          </Button>
        </form>
        <div className="my-6 flex w-full items-center">
          <div className="grow border-t border-core-gray-300" />
          <span className="mx-4 text-sm text-core-gray-500">OR</span>
          <div className="grow border-t border-core-gray-300" />
        </div>
        <div className="w-full text-center">
          <span className="text-core-gray-700">Already have an account? </span>
          <InternalLink href="/login" className="text-primary-500 font-semibold text-info-600 underline">
            Log In
          </InternalLink>
        </div>
      </div>
    </div>
  )
}
